import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, DestroyRef, ElementRef, OnDestroy, OnInit, ViewChild, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { filter, firstValueFrom } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import * as L from 'leaflet';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSnackBar, MatSnackBarHorizontalPosition, MatSnackBarVerticalPosition } from '@angular/material/snack-bar';
import { AuthorizedApiService, CanAccessDirective } from '@core';
import { AuthorizationService } from '@core/service/authorization.service';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { BreadcrumbComponent } from '@shared/components/breadcrumb/breadcrumb.component';
import { ConfirmDeleteDialogComponent } from '@shared/components/confirm-delete-dialog/confirm-delete-dialog.component';
import { DateTimePickerFieldComponent } from '@shared/components/date-time-picker-field/date-time-picker-field.component';
import { LoadingStateComponent } from '@shared/components/loading-state/loading-state.component';
import { extractApiErrorMessage } from '@shared/utils/api-error.util';
import { EmployeeRecord } from '../../organization/employees/employee.interface';
import { TrackingPointCreatePayload, TrackingPointRecord, TrackingPointUpdatePayload } from './tracking-point.interface';

type TrackingPointWorkspaceMode = 'view' | 'create' | 'edit';

interface EmployeesResponse {
  data?: EmployeeRecord[];
  items?: EmployeeRecord[];
  results?: EmployeeRecord[];
  employees?: EmployeeRecord[];
}

interface TrackingPointFormValue {
  trackedAt: string;
  lat: number | '';
  lng: number | '';
  accuracy: number | '';
  deviceId: string;
  deviceVersion: string;
  rawPayloadText: string;
  outsideWorkday: boolean;
  isActive: boolean;
}

@Component({
  selector: 'app-tracking-point-detail',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    BreadcrumbComponent,
    LoadingStateComponent,
    DateTimePickerFieldComponent,
    CanAccessDirective,
    MatButtonModule,
    MatCardModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatSelectModule,
    MatSlideToggleModule,
    TranslateModule,
  ],
  templateUrl: './tracking-point-detail.component.html',
  styleUrl: './tracking-point-detail.component.scss',
})
export class TrackingPointDetailComponent implements OnInit, AfterViewInit, OnDestroy {
  private readonly apiService = inject(AuthorizedApiService);
  private readonly authorizationService = inject(AuthorizationService);
  private readonly dialog = inject(MatDialog);
  private readonly snackBar = inject(MatSnackBar);
  private readonly translate = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);

  @ViewChild('pointMap') private pointMapRef?: ElementRef<HTMLDivElement>;

  isLoading = true;
  isSaving = false;
  isDeleting = false;
  mode: TrackingPointWorkspaceMode = 'view';
  employees: EmployeeRecord[] = [];
  selectedTrackingPointId: string | null = null;
  selectedTrackingPoint: TrackingPointRecord | null = null;
  form: TrackingPointFormValue = this.createEmptyForm();

  readonly readAccess = { resource: 'tracking', action: 'read' } as const;
  readonly createAccess = { resource: 'tracking', action: 'create' } as const;
  readonly updateAccess = { resource: 'tracking', action: 'update' } as const;
  readonly deleteAccess = { resource: 'tracking', action: 'delete' } as const;

  private map: L.Map | null = null;
  private tileLayer: L.TileLayer | null = null;
  private pointMarker: L.Marker | null = null;
  private readonly defaultCenter: L.LatLngTuple = [21.1619, -86.8515];
  private syncingMapFromForm = false;

  ngOnInit(): void {
    this.router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe(() => {
        void this.syncWithRoute();
      });

    void this.loadBaseData().then(() => this.syncWithRoute());
  }

  ngAfterViewInit(): void {
    setTimeout(() => this.renderMap(), 0);
  }

  ngOnDestroy(): void {
    this.destroyMap();
  }

  get isCreateMode(): boolean {
    return this.mode === 'create';
  }

  get isEditMode(): boolean {
    return this.mode === 'edit';
  }

  get isViewMode(): boolean {
    return this.mode === 'view';
  }

  get batteryLevelLabel(): string {
    return this.formatBatteryLevel(this.selectedTrackingPoint?.rawPayload?.['batteryLevel']);
  }

  get batteryLevelValue(): number | null {
    return this.normalizeBatteryLevel(this.selectedTrackingPoint?.rawPayload?.['batteryLevel']);
  }

  get batteryLevelClass(): string {
    const level = this.batteryLevelValue;
    if (level === null) {
      return 'tracking-battery--unknown';
    }
    if (level <= 15) {
      return 'tracking-battery--critical';
    }
    if (level <= 35) {
      return 'tracking-battery--low';
    }
    return 'tracking-battery--normal';
  }

  get detailTitle(): string {
    if (this.isCreateMode) {
      return this.t('trackingPointsCatalog.detail.createTitle');
    }
    if (this.isEditMode) {
      return this.t('trackingPointsCatalog.detail.editTitle');
    }
    return this.t('trackingPointsCatalog.detail.title');
  }

  get detailDescription(): string {
    if (this.isCreateMode) {
      return this.t('trackingPointsCatalog.detail.createDescription');
    }
    if (this.isEditMode) {
      return this.t('trackingPointsCatalog.detail.editDescription');
    }
    return this.t('trackingPointsCatalog.detail.description');
  }

  get canSave(): boolean {
    return !this.validateForm();
  }

  get coordinatesLabel(): string {
    if (this.form.lat === '' || this.form.lng === '') {
      return this.t('trackingPointsCatalog.labels.pendingCoordinates');
    }
    return `${Number(this.form.lat).toFixed(6)}, ${Number(this.form.lng).toFixed(6)}`;
  }

  goBackToList(): void {
    void this.router.navigate(['/attendance/tracking-points']);
  }

  startEdit(): void {
    if (!this.selectedTrackingPoint || !this.hasAccess('update')) {
      return;
    }
    this.mode = 'edit';
    this.form = this.createFormFromPoint(this.selectedTrackingPoint);
    this.syncMapInteractivity();
    void this.router.navigate(['/attendance/tracking-points', this.selectedTrackingPoint.trackingPointId, 'edit']);
  }

  cancelEdit(): void {
    if (this.selectedTrackingPoint) {
      this.mode = 'view';
      this.form = this.createFormFromPoint(this.selectedTrackingPoint);
      this.syncMapFromFormValues();
      this.syncMapInteractivity();
      void this.router.navigate(['/attendance/tracking-points', this.selectedTrackingPoint.trackingPointId]);
      return;
    }

    this.goBackToList();
  }

  onCoordinateInput(): void {
    this.syncMapFromFormValues();
  }

  async save(): Promise<void> {
    const validationMessage = this.validateForm();
    if (validationMessage) {
      this.showNotification('snackbar-warning', validationMessage, 'bottom', 'center');
      return;
    }

    this.isSaving = true;
    try {
      if (this.isCreateMode) {
        const created = await firstValueFrom(
          this.apiService.post<TrackingPointRecord>('attendance/tracking-points', this.buildCreatePayload())
        );
        this.showNotification('snackbar-success', this.t('trackingPointsCatalog.notifications.created'), 'bottom', 'center');
        void this.router.navigate(['/attendance/tracking-points', created.trackingPointId]);
        return;
      }

      if (this.isEditMode && this.selectedTrackingPointId) {
        await firstValueFrom(
          this.apiService.patch(`attendance/tracking-points/${this.selectedTrackingPointId}`, this.buildUpdatePayload())
        );
        this.showNotification('snackbar-success', this.t('trackingPointsCatalog.notifications.updated'), 'bottom', 'center');
        void this.router.navigate(['/attendance/tracking-points', this.selectedTrackingPointId]);
      }
    } catch (error) {
      console.error('Error saving tracking point', error);
      this.showNotification(
        'snackbar-danger',
        extractApiErrorMessage(error, this.t('trackingPointsCatalog.notifications.saveError')),
        'bottom',
        'center'
      );
    } finally {
      this.isSaving = false;
    }
  }

  deleteSelected(): void {
    if (!this.selectedTrackingPoint || !this.hasAccess('delete')) {
      return;
    }

    const dialogRef = this.dialog.open(ConfirmDeleteDialogComponent, {
      width: '420px',
      data: {
        title: this.t('trackingPointsCatalog.dialog.deleteTitle'),
        message: this.t('trackingPointsCatalog.dialog.deleteMessage', { point: this.selectedTrackingPoint.employeeNumber }),
        confirmLabel: this.t('trackingPointsCatalog.dialog.deleteConfirm'),
        cancelLabel: this.t('common.cancel'),
      },
    });

    dialogRef.afterClosed().subscribe(async (confirmed: boolean) => {
      if (!confirmed || !this.selectedTrackingPointId) {
        return;
      }

      this.isDeleting = true;
      try {
        await firstValueFrom(this.apiService.delete(`attendance/tracking-points/${this.selectedTrackingPointId}`));
        this.showNotification('snackbar-success', this.t('trackingPointsCatalog.notifications.deleted'), 'bottom', 'center');
        void this.router.navigate(['/attendance/tracking-points']);
      } catch (error) {
        console.error('Error deleting tracking point', error);
        this.showNotification(
          'snackbar-danger',
          extractApiErrorMessage(error, this.t('trackingPointsCatalog.notifications.deleteError')),
          'bottom',
          'center'
        );
      } finally {
        this.isDeleting = false;
      }
    });
  }

  private async loadBaseData(): Promise<void> {
    this.isLoading = true;
    try {
      const response = await firstValueFrom(this.apiService.get<EmployeeRecord[] | EmployeesResponse>('employees'));
      this.employees = this.extractEmployees(response)
        .filter((employee) => employee.isActive !== false)
        .sort((left, right) => (left.fullName || '').localeCompare(right.fullName || ''));
    } catch (error) {
      console.error('Error loading employees for tracking points', error);
      this.employees = [];
    } finally {
      this.isLoading = false;
    }
  }

  private async syncWithRoute(): Promise<void> {
    const trackingPointId = this.route.snapshot.paramMap.get('trackingPointId');
    const currentUrl = this.router.url.split('?')[0];
    const isCreateRoute = currentUrl.endsWith('/attendance/tracking-points/new');
    const isEditRoute = /\/attendance\/tracking-points\/[^/]+\/edit$/.test(currentUrl);

    if (isCreateRoute) {
      this.enterCreateMode();
      return;
    }

    if (!trackingPointId) {
      void this.router.navigate(['/attendance/tracking-points']);
      return;
    }

    this.isLoading = true;
    try {
      const point = await firstValueFrom(this.apiService.get<TrackingPointRecord>(`attendance/tracking-points/${trackingPointId}`));
      this.selectedTrackingPoint = this.normalizePoint(point);
      this.selectedTrackingPointId = this.selectedTrackingPoint.trackingPointId;
      this.form = this.createFormFromPoint(this.selectedTrackingPoint);
      this.mode = isEditRoute ? 'edit' : 'view';
      this.syncMapFromFormValues();
      this.syncMapInteractivity();
    } catch (error) {
      console.error('Error loading tracking point detail', error);
      this.showNotification(
        'snackbar-danger',
        extractApiErrorMessage(error, this.t('trackingPointsCatalog.notifications.loadDetailError')),
        'bottom',
        'center'
      );
      void this.router.navigate(['/attendance/tracking-points']);
    } finally {
      this.isLoading = false;
      setTimeout(() => this.renderMap(), 0);
    }
  }

  private enterCreateMode(): void {
    this.mode = 'create';
    this.selectedTrackingPoint = null;
    this.selectedTrackingPointId = null;
    this.form = this.createEmptyForm();
    this.syncMapFromFormValues();
    this.syncMapInteractivity();
    setTimeout(() => this.renderMap(), 0);
  }

  private createEmptyForm(): TrackingPointFormValue {
    return {
      trackedAt: this.toDateTimeLocal(new Date().toISOString()),
      lat: '',
      lng: '',
      accuracy: '',
      deviceId: '',
      deviceVersion: '',
      rawPayloadText: '{\n  "batteryLevel": 82\n}',
      outsideWorkday: false,
      isActive: true,
    };
  }

  private createFormFromPoint(point: TrackingPointRecord): TrackingPointFormValue {
    return {
      trackedAt: this.toDateTimeLocal(point.trackedAt),
      lat: point.lat,
      lng: point.lng,
      accuracy: point.accuracy ?? '',
      deviceId: point.deviceId || '',
      deviceVersion: point.deviceVersion || '',
      rawPayloadText: JSON.stringify(point.rawPayload || {}, null, 2),
      outsideWorkday: point.outsideWorkday === true,
      isActive: point.isActive !== false,
    };
  }

  private buildCreatePayload(): TrackingPointCreatePayload {
    return {
      trackedAt: new Date(this.form.trackedAt).toISOString(),
      lat: Number(this.form.lat),
      lng: Number(this.form.lng),
      ...(this.form.accuracy !== '' ? { accuracy: Number(this.form.accuracy) } : {}),
      ...(this.form.deviceId.trim() ? { deviceId: this.form.deviceId.trim() } : {}),
      ...(this.form.deviceVersion.trim() ? { deviceVersion: this.form.deviceVersion.trim() } : {}),
      ...(this.parseRawPayload() ? { rawPayload: this.parseRawPayload()! } : {}),
    };
  }

  private buildUpdatePayload(): TrackingPointUpdatePayload {
    return {
      ...this.buildCreatePayload(),
      outsideWorkday: this.form.outsideWorkday,
      isActive: this.form.isActive,
    };
  }

  private validateForm(): string | null {
    if (!this.form.trackedAt) {
      return this.t('trackingPointsCatalog.notifications.validationTrackedAt');
    }
    if (this.form.lat === '' || this.form.lng === '') {
      return this.t('trackingPointsCatalog.notifications.validationCoordinates');
    }
    if (this.form.rawPayloadText.trim()) {
      try {
        JSON.parse(this.form.rawPayloadText);
      } catch {
        return this.t('trackingPointsCatalog.notifications.validationRawPayload');
      }
    }
    return null;
  }

  private parseRawPayload(): Record<string, unknown> | null {
    if (!this.form.rawPayloadText.trim()) {
      return null;
    }
    return JSON.parse(this.form.rawPayloadText);
  }

  private extractEmployees(response: EmployeeRecord[] | EmployeesResponse): EmployeeRecord[] {
    if (Array.isArray(response)) {
      return response;
    }
    return response.items || response.data || response.results || response.employees || [];
  }

  private normalizePoint(point: Partial<TrackingPointRecord>): TrackingPointRecord {
    const employee = this.employees.find((item) => item.employeeId === point.employeeId);
    const lat = Number(point.lat || 0);
    const lng = Number(point.lng || 0);
    const isActive = point.isActive !== false;
    const outsideWorkday = point.outsideWorkday === true;

    return {
      trackingPointId: point.trackingPointId || point._id || crypto.randomUUID(),
      center: point.center || '',
      employeeId: point.employeeId || '',
      employeeNumber: point.employeeNumber || employee?.employeeNumber || '-',
      companiesRecordId: point.companiesRecordId || '',
      orgUnitId: point.orgUnitId || null,
      orgPath: point.orgPath || null,
      trackedAt: point.trackedAt || '',
      lat,
      lng,
      accuracy: point.accuracy ?? null,
      deviceId: point.deviceId || null,
      deviceVersion: point.deviceVersion || null,
      outsideWorkday,
      rawPayload: point.rawPayload || null,
      isActive,
      employeeLabel: employee?.fullName || point.employeeNumber || '-',
      trackedAtLabel: this.formatDateTime(point.trackedAt),
      coordinatesLabel: `${lat.toFixed(6)}, ${lng.toFixed(6)}`,
      outsideWorkdayLabel: outsideWorkday ? this.t('common.yes') : this.t('common.no'),
      activeLabel: isActive ? this.t('common.status.active') : this.t('common.status.inactive'),
    };
  }

  private renderMap(): void {
    const container = this.pointMapRef?.nativeElement;
    if (!container) {
      return;
    }

    if (!this.map) {
      this.map = L.map(container, {
        zoomControl: true,
        attributionControl: true,
      }).setView(this.defaultCenter, 13);

      this.tileLayer = L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        maxZoom: 19,
        attribution: '&copy; OpenStreetMap contributors',
      });
      this.tileLayer.addTo(this.map);

      this.map.on('click', (event: L.LeafletMouseEvent) => {
        if (this.isViewMode) {
          return;
        }
        this.setCoordinates(event.latlng.lat, event.latlng.lng);
      });
    }

    this.ensureMapLayers();
    this.syncMapFromFormValues();
    this.syncMapInteractivity();
    setTimeout(() => this.map?.invalidateSize(), 0);
  }

  private formatBatteryLevel(value: unknown): string {
    const batteryLevel = this.normalizeBatteryLevel(value);
    return batteryLevel === null ? '-' : `${batteryLevel}%`;
  }

  private normalizeBatteryLevel(value: unknown): number | null {
    const batteryLevel = Number(value);
    return Number.isFinite(batteryLevel) && batteryLevel >= 0
      ? Math.round(Math.min(100, batteryLevel))
      : null;
  }

  private ensureMapLayers(): void {
    if (!this.map) {
      return;
    }

    if (!this.pointMarker) {
      this.pointMarker = L.marker(this.defaultCenter, {
        draggable: !this.isViewMode,
        icon: this.buildMarkerIcon(),
      }).addTo(this.map);

      this.pointMarker.on('dragend', () => {
        const latLng = this.pointMarker?.getLatLng();
        if (!latLng || this.isViewMode) {
          return;
        }
        this.setCoordinates(latLng.lat, latLng.lng);
      });
    }
  }

  private syncMapFromFormValues(): void {
    if (!this.map || !this.pointMarker) {
      return;
    }

    const hasCoordinates = this.form.lat !== '' && this.form.lng !== '';
    const lat = hasCoordinates ? Number(this.form.lat) : this.defaultCenter[0];
    const lng = hasCoordinates ? Number(this.form.lng) : this.defaultCenter[1];
    const center = L.latLng(lat, lng);

    this.syncingMapFromForm = true;
    this.pointMarker.setLatLng(center);
    this.map.setView(center, hasCoordinates ? 16 : 13);
    this.syncingMapFromForm = false;
  }

  private syncMapInteractivity(): void {
    if (!this.pointMarker) {
      return;
    }

    if (this.isViewMode) {
      this.pointMarker.dragging?.disable();
    } else {
      this.pointMarker.dragging?.enable();
    }
  }

  private setCoordinates(lat: number, lng: number): void {
    if (this.syncingMapFromForm) {
      return;
    }
    this.form.lat = Number(lat.toFixed(6));
    this.form.lng = Number(lng.toFixed(6));
    this.syncMapFromFormValues();
  }

  private buildMarkerIcon(): L.DivIcon {
    return L.divIcon({
      className: 'tracking-point-marker',
      html: '<span class="material-icons">location_on</span>',
      iconSize: [36, 36],
      iconAnchor: [18, 18],
    });
  }

  private destroyMap(): void {
    this.pointMarker = null;
    this.tileLayer = null;
    if (this.map) {
      this.map.remove();
      this.map = null;
    }
  }

  private toDateTimeLocal(value?: string | null): string {
    if (!value) {
      return '';
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return '';
    }
    const pad = (part: number) => `${part}`.padStart(2, '0');
    return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
  }

  private formatDateTime(value?: string | null): string {
    if (!value) {
      return '-';
    }
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
      return value;
    }
    const locale = this.translate.currentLang?.startsWith('en') ? 'en-US' : 'es-MX';
    return new Intl.DateTimeFormat(locale, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  }

  private hasAccess(action: 'create' | 'read' | 'update' | 'delete' | 'access'): boolean {
    return this.authorizationService.hasAccess({ resource: 'tracking', action });
  }

  private showNotification(
    colorName: string,
    text: string,
    placementFrom: MatSnackBarVerticalPosition,
    placementAlign: MatSnackBarHorizontalPosition
  ): void {
    this.snackBar.open(text, '', {
      duration: 2500,
      verticalPosition: placementFrom,
      horizontalPosition: placementAlign,
      panelClass: colorName,
    });
  }

  private t(key: string, params?: Record<string, unknown>): string {
    return this.translate.instant(key, params);
  }
}


